import Graph, { MultiGraph } from 'graphology';
import { DateTime } from 'luxon';

function _typeof(o) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) {
    return typeof o;
  } : function (o) {
    return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
  }, _typeof(o);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function deserializer(_, value) {
  if (Array.isArray(value) && value.length === 3 && value[0] === "<<SET" && value[2] === "SET>>") {
    return new Set(gephiLiteParse(value[1]));
  }
  if (Array.isArray(value) && value.length === 3 && value[0] === "<<Function" && value[2] === "Function>>") {
    return new Function("return ".concat(value[1]))();
  }
  if (value && _typeof(value) === "object" && "nodes" in value && "edges" in value) {
    return Graph.from(value);
  }
  return value;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function serializer(_, value) {
  if (value instanceof Set) {
    return ["<<SET", gephiLiteStringify(Array.from(value)), "SET>>"];
  }
  if (value instanceof Function) {
    return ["<<Function", value.toString(), "Function>>"];
  }
  return value;
}

/**
 * Use the following functions to serialize/deserialize data structures that may
 * include Sets, serializable functions & graph.
 */
function gephiLiteStringify(value) {
  return JSON.stringify(value, serializer);
}

/**
 * Deserialize JSON data in JS, using custom reviver.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function gephiLiteParse(value) {
  return JSON.parse(value, deserializer);
}

function _toPrimitive(t, r) {
  if ("object" != typeof t || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r || "default");
    if ("object" != typeof i) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}

function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return "symbol" == typeof i ? i : i + "";
}

function _defineProperty(e, r, t) {
  return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
    value: t,
    enumerable: !0,
    configurable: !0,
    writable: !0
  }) : e[r] = t, e;
}

function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread2(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}

/**
 * BASE TYPES:
 * ***********
 */

var SCALAR_TYPES = new Set(["boolean", "number", "string", "undefined"]);
var ITEM_TYPES = ["nodes", "edges"];

/**
 * ITEMS DATA:
 * ***********
 */

/**
 * MODEL:
 * ******
 */

/**
 * Describes how Gephi Lite should interpret a nodes or edges field.
 */
/* eslint-disable @typescript-eslint/no-empty-object-type */

/**
 * GRAPHS:
 * *******
 */

/**
 * A Graphology graph, without any attribute, to just describe the topology of a graph.
 */

/**
 * STATES:
 * *******
 */

/**
 * A canonical structure that contains the topology of a graph, its nodes and edges attributes and rendering attributes,
 * as well as the models to know how to interpret all the attributes.
 */

// Dynamic data for nodes which are recomputed from topology (like degree)

function getEmptyGraphDataset() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$graphType = _ref.graphType,
    graphType = _ref$graphType === void 0 ? "mixed" : _ref$graphType;
  return {
    nodeData: {},
    edgeData: {},
    layout: {},
    metadata: {
      title: ""
    },
    nodeFields: [],
    edgeFields: [],
    fullGraph: new MultiGraph({
      type: graphType
    })
  };
}
function serializeDataset(dataset) {
  return dataset.fullGraph ? _objectSpread2(_objectSpread2({}, dataset), {}, {
    fullGraph: dataset.fullGraph["export"]()
  }) : dataset;
}
function deserializeDataset(dataset) {
  if (!dataset.fullGraph) return dataset;
  var fullGraph = new MultiGraph(dataset.fullGraph.options);
  fullGraph["import"](dataset.fullGraph);
  return _objectSpread2(_objectSpread2({}, dataset), {}, {
    fullGraph: fullGraph
  });
}
function datasetToString(dataset) {
  return gephiLiteStringify(serializeDataset(dataset));
}
function parseDataset(rawDataset) {
  try {
    // TODO:
    // Validate the actual data
    return deserializeDataset(gephiLiteParse(rawDataset));
  } catch (e) {
    console.error(e);
    return null;
  }
}

function toScalar(o) {
  if (SCALAR_TYPES.has(_typeof(o))) return o;

  // some special cases
  if (o instanceof Date) return o.toString();
  if (o instanceof Object) return JSON.stringify(o);
  if (o === null) return undefined;
  // fallback to toString
  return "".concat(o);
}
function toNumber(o) {
  if (typeof o === "number" && !isNaN(o)) return o;
  if (typeof o === "string") {
    var n = +o;
    if (!isNaN(n)) return n;
  }
  return undefined;
}
function toString(o) {
  if (typeof o === "string") return o;
  if (typeof o === "number") return o + "";
  if (typeof o === "boolean") return o.toString();
  return undefined;
}
function toStringArray(o, separator) {
  var oAsString = toString(o);
  if (oAsString) return oAsString.split(separator);
  return undefined;
}
function toDate(o, format) {
  var oAsString = toString(o);
  if (oAsString) {
    try {
      if (format) {
        var d = DateTime.fromFormat(oAsString, format);
        return d.isValid ? d : undefined;
      } else {
        var _d = DateTime.fromISO(oAsString);
        return _d.isValid ? _d : undefined;
      }
    } catch (error) {
      if (error instanceof RangeError) return undefined;else throw error;
    }
  }
  return undefined;
}
function notEmpty(value) {
  return value !== null && value !== undefined;
}

// Sizes management:

// Colors management:

// Labels management:

// Z-index management:

/**
 * Describes how each visual variable should be used to render the graph in Gephi Lite.
 */

var APPEARANCE_ITEM_TYPES = {
  backgroundColor: null,
  layoutGridColor: null,
  nodesSize: "nodes",
  nodesColor: "nodes",
  nodesShadingColor: "nodes",
  nodesLabel: "nodes",
  nodesLabelSize: "nodes",
  nodesLabelEllipsis: "nodes",
  nodesImage: "nodes",
  showEdges: "edges",
  edgesSize: "edges",
  edgesColor: "edges",
  edgesShadingColor: "edges",
  edgesLabel: "edges",
  edgesLabelSize: "edges",
  edgesLabelEllipsis: "edges",
  edgesZIndex: "edges"
};

/**
 * This state contains the visual getters, i.e. the functions to get a node or
 * edge size, color or label:
 */

/**
 * This types override RenderingData from sigma, with additional attributes used
 * in Gephi Lite:
 */

var DEFAULT_NODE_COLOR = "#999999";
var DEFAULT_EDGE_COLOR = "#cccccc";
var DEFAULT_NODE_SIZE = 20;
var DEFAULT_EDGE_SIZE = 6;
var DEFAULT_NODE_LABEL_SIZE = 14;
var DEFAULT_EDGE_LABEL_SIZE = 14;
var DEFAULT_BACKGROUND_COLOR = "#FFFFFF00";
var DEFAULT_LAYOUT_GRID_COLOR = "#666666";
var DEFAULT_SHADING_COLOR = "#ffffff";
function getEmptyAppearanceState() {
  return {
    showEdges: {
      value: true
    },
    nodesSize: {
      type: "fixed",
      value: DEFAULT_NODE_SIZE
    },
    edgesSize: {
      type: "fixed",
      value: DEFAULT_EDGE_SIZE
    },
    backgroundColor: DEFAULT_BACKGROUND_COLOR,
    layoutGridColor: DEFAULT_LAYOUT_GRID_COLOR,
    nodesColor: {
      type: "fixed",
      value: DEFAULT_NODE_COLOR
    },
    edgesColor: {
      type: "fixed",
      value: DEFAULT_EDGE_COLOR
    },
    nodesLabel: {
      type: "none"
    },
    edgesLabel: {
      type: "none"
    },
    nodesLabelSize: {
      type: "fixed",
      value: DEFAULT_NODE_LABEL_SIZE,
      zoomCorrelation: 0,
      density: 1
    },
    edgesLabelSize: {
      type: "fixed",
      value: DEFAULT_EDGE_LABEL_SIZE,
      zoomCorrelation: 0,
      density: 1
    },
    nodesLabelEllipsis: {
      type: "ellipsis",
      enabled: false,
      maxLength: 25
    },
    edgesLabelEllipsis: {
      type: "ellipsis",
      enabled: false,
      maxLength: 25
    },
    nodesImage: {
      type: "none"
    },
    edgesZIndex: {
      type: "none"
    }
  };
}

/**
 * Appearance lifecycle helpers (state serialization / deserialization):
 */
function serializeAppearanceState(appearance) {
  return gephiLiteStringify(appearance);
}
function parseAppearanceState(rawAppearance) {
  try {
    // TODO:
    // Validate the actual data
    return _objectSpread2(_objectSpread2({}, getEmptyAppearanceState()), gephiLiteParse(rawAppearance));
  } catch (_e) {
    return null;
  }
}

/**
 * Returns an empty filters state:
 */
function getEmptyFiltersState() {
  return {
    filters: []
  };
}

/**
 * Filters lifecycle helpers (state serialization / deserialization):
 */
function serializeFiltersState(filters) {
  return gephiLiteStringify(filters);
}
function parseFiltersState(rawFilters) {
  try {
    // TODO:
    // Validate the actual data
    return gephiLiteParse(rawFilters);
  } catch (e) {
    console.error(e);
    return null;
  }
}

export { APPEARANCE_ITEM_TYPES, DEFAULT_BACKGROUND_COLOR, DEFAULT_EDGE_COLOR, DEFAULT_EDGE_LABEL_SIZE, DEFAULT_EDGE_SIZE, DEFAULT_LAYOUT_GRID_COLOR, DEFAULT_NODE_COLOR, DEFAULT_NODE_LABEL_SIZE, DEFAULT_NODE_SIZE, DEFAULT_SHADING_COLOR, ITEM_TYPES, SCALAR_TYPES, datasetToString, deserializeDataset, deserializer, gephiLiteParse, gephiLiteStringify, getEmptyAppearanceState, getEmptyFiltersState, getEmptyGraphDataset, notEmpty, parseAppearanceState, parseDataset, parseFiltersState, serializeAppearanceState, serializeDataset, serializeFiltersState, serializer, toDate, toNumber, toScalar, toString, toStringArray };
